/**
 * Redux slice for workout routines
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  WorkoutRoutineState,
  WorkoutRoutine,
  WorkoutRoutineBase,
  FavoriteWorkoutRoutine} from '../../types/workout-routine';
import * as workoutRoutineService from '../../services/firebase/workout-routine.service';

// Initial state
const initialState: WorkoutRoutineState = {
  routines: {},
  userPublicRoutines: [],
  userPrivateRoutines: [],
  allPublicRoutines: [],
  favoriteRoutines: [],
  wereFavRoutinesFetched: false,
  currentRoutine: null,
  availableTags: [], // Will be populated from status quo or config
  isLoading: false,
  error: null,
};

// Async thunks
export const createWorkoutRoutine = createAsyncThunk<
  string,
  { userId: string; routineData: WorkoutRoutineBase },
  { rejectValue: string }
>('workoutRoutine/createWorkoutRoutine', async ({ userId, routineData }, { rejectWithValue }) => {
  try {
    const routineKey = await workoutRoutineService.createWorkoutRoutine(userId, routineData);
    return routineKey;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create workout routine');
  }
});

export const updateWorkoutRoutine = createAsyncThunk<
  void,
  { routineKey: string; updatedData: Partial<WorkoutRoutineBase> },
  { rejectValue: string }
>('workoutRoutine/updateWorkoutRoutine', async ({ routineKey, updatedData }, { rejectWithValue }) => {
  try {
    await workoutRoutineService.updateWorkoutRoutine(routineKey, updatedData);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update workout routine');
  }
});

export const fetchWorkoutRoutine = createAsyncThunk<
  WorkoutRoutine,
  string,
  { rejectValue: string }
>('workoutRoutine/fetchWorkoutRoutine', async (routineKey, { rejectWithValue }) => {
  try {
    const routine = await workoutRoutineService.getWorkoutRoutine(routineKey);
    if (!routine) {
      return rejectWithValue('Workout routine not found');
    }
    return routine;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch workout routine');
  }
});

export const fetchFavoriteRoutines = createAsyncThunk<
  FavoriteWorkoutRoutine[],
  string,
  { rejectValue: string }
>('workoutRoutine/fetchFavoriteRoutines', async (userId, { rejectWithValue }) => {
  try {
    const favoriteRoutines = await workoutRoutineService.getFavoriteRoutines(userId);
    return favoriteRoutines;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch favorite routines');
  }
});

export const setFavoriteRoutines = createAsyncThunk<
  void,
  { userId: string; favoriteRoutines: FavoriteWorkoutRoutine[] },
  { rejectValue: string }
>('workoutRoutine/setFavoriteRoutines', async ({ userId, favoriteRoutines }, { rejectWithValue }) => {
  try {
    await workoutRoutineService.setFavoriteRoutines(userId, favoriteRoutines);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to set favorite routines');
  }
});

// Like functionality removed - use the unified likes.service.ts instead

export const uploadCoverPhoto = createAsyncThunk<
  string,
  { userId: string; routineKey: string; photoUri: string; aspectRatio?: number },
  { rejectValue: string }
>('workoutRoutine/uploadCoverPhoto', async ({ userId, routineKey, photoUri, aspectRatio }, { rejectWithValue }) => {
  try {
    const downloadURL = await workoutRoutineService.addCoverPhoto(userId, routineKey, photoUri, aspectRatio);
    return downloadURL;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to upload cover photo');
  }
});

// Create slice
const workoutRoutineSlice = createSlice({
  name: 'workoutRoutine',
  initialState,
  reducers: {
    // Set available tags (from config or status quo)
    setAvailableTags: (state, action: PayloadAction<string[]>) => {
      state.availableTags = action.payload;
    },
    
    // Set user's public routines
    setUserPublicRoutines: (state, action: PayloadAction<{ routines: Record<string, any> }>) => {
      const { routines } = action.payload;
      state.userPublicRoutines = Object.keys(routines);
      
      // Add to routines record
      Object.keys(routines).forEach(key => {
        state.routines[key] = {
          ...routines[key],
          routineKey: key
        };
      });
    },
    
    // Set user's private routines
    setUserPrivateRoutines: (state, action: PayloadAction<{ routines: Record<string, any> }>) => {
      const { routines } = action.payload;
      state.userPrivateRoutines = Object.keys(routines);
      
      // Add to routines record
      Object.keys(routines).forEach(key => {
        state.routines[key] = {
          ...routines[key],
          routineKey: key
        };
      });
    },
    
    // Set all public routines
    setAllPublicRoutines: (state, action: PayloadAction<{ routines: Record<string, any> }>) => {
      const { routines } = action.payload;
      state.allPublicRoutines = Object.keys(routines);
      
      // Add to routines record
      Object.keys(routines).forEach(key => {
        state.routines[key] = {
          ...routines[key],
          routineKey: key
        };
      });
    },
    
    // Update routine data in store after subscribe callback
    updateRoutineData: (state, action: PayloadAction<WorkoutRoutine>) => {
      const routine = action.payload;
      state.routines[routine.routineKey] = routine;
      
      if (state.currentRoutine?.routineKey === routine.routineKey) {
        state.currentRoutine = routine;
      }
    },
    
    // Clear current routine
    clearCurrentRoutine: (state) => {
      state.currentRoutine = null;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Like functionality removed - use the unified likes.service.ts instead
    
    // Local toggle favorite (for immediate UI feedback)
    setLocalFavorite: (state, action: PayloadAction<{ routineKey: string; author: string; isFavorite: boolean }>) => {
      const { routineKey, author, isFavorite } = action.payload;
      
      if (isFavorite) {
        // Add to favorites if not already there
        if (!state.favoriteRoutines.some(r => r.routineKey === routineKey)) {
          state.favoriteRoutines.push({ routineKey, author });
        }
      } else {
        // Remove from favorites
        state.favoriteRoutines = state.favoriteRoutines.filter(r => r.routineKey !== routineKey);
      }
    },
  },
  extraReducers: (builder) => {
    // createWorkoutRoutine
    builder.addCase(createWorkoutRoutine.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createWorkoutRoutine.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createWorkoutRoutine.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to create workout routine';
    });
    
    // updateWorkoutRoutine
    builder.addCase(updateWorkoutRoutine.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateWorkoutRoutine.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateWorkoutRoutine.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to update workout routine';
    });
    
    // fetchWorkoutRoutine
    builder.addCase(fetchWorkoutRoutine.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchWorkoutRoutine.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentRoutine = action.payload;
      state.routines[action.payload.routineKey] = action.payload;
    });
    builder.addCase(fetchWorkoutRoutine.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch workout routine';
    });
    
    // fetchFavoriteRoutines
    builder.addCase(fetchFavoriteRoutines.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchFavoriteRoutines.fulfilled, (state, action) => {
      state.isLoading = false;
      state.favoriteRoutines = action.payload;
      state.wereFavRoutinesFetched = true;
    });
    builder.addCase(fetchFavoriteRoutines.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch favorite routines';
    });
    
    // setFavoriteRoutines
    builder.addCase(setFavoriteRoutines.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(setFavoriteRoutines.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(setFavoriteRoutines.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to set favorite routines';
    });
    
    // Like functionality removed - use the unified likes.service.ts instead
    
    // uploadCoverPhoto
    builder.addCase(uploadCoverPhoto.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(uploadCoverPhoto.fulfilled, (state, action) => {
      state.isLoading = false;
      
      // Update cover photo URL in current routine if available
      if (state.currentRoutine) {
        if (!state.currentRoutine.coverPhoto) {
          state.currentRoutine.coverPhoto = {};
        }
        state.currentRoutine.coverPhoto.url = action.payload;
      }
    });
    builder.addCase(uploadCoverPhoto.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to upload cover photo';
    });
  },
});

// Export actions
export const { 
  setAvailableTags,
  setUserPublicRoutines,
  setUserPrivateRoutines,
  setAllPublicRoutines,
  updateRoutineData,
  clearCurrentRoutine,
  clearError,
  setLocalFavorite
} = workoutRoutineSlice.actions;

// Export reducer
export default workoutRoutineSlice.reducer;